<template>
  <div
    class="col-12 festival_daily_schedule"
    :class="{ 'is-rtl': isRtl, 'is-readonly': readonly }"
  >
    <div ref="scheduleHeader" class="schedule_header">
      <div class="schedule_header_icon">
        <i class="fal fa-calendar-alt"></i>
      </div>
      <div class="schedule_header_text">
        <h5 class="schedule_title">{{ $t("MESSAGES.dailySchedule") }}</h5>
        <p class="schedule_subtitle">
          {{
            showSchedule
              ? $t("MESSAGES.dailyScheduleHint")
              : $t("MESSAGES.dailySchedulePrerequisitesHint")
          }}
        </p>
      </div>
    </div>

    <div v-if="!showSchedule" class="schedule_placeholder">
      <i class="fal fa-info-circle"></i>
      <span>{{ $t("MESSAGES.dailySchedulePrerequisitesHint") }}</span>
    </div>

    <template v-else>
      <div
        v-for="(day, dayIndex) in localSchedules"
        :key="day.date"
        :ref="`day-${day.date}`"
        class="day_card"
        :class="{
          'day_card--inactive': !day.is_active,
          'day_card--error': highlightedDayDate === day.date,
        }"
      >
        <div class="day_card_header">
          <div class="day_date_block">
            <span class="day_date_chip">{{ formatDayChip(day.date) }}</span>
            <span class="day_date">{{ formatDisplayDate(day.date) }}</span>
          </div>

          <div class="day_header_actions">
            <button
              v-if="!readonly && day.is_active && canApplyDayTimes(dayIndex)"
              type="button"
              class="apply_times_btn"
              :title="$t('MESSAGES.applyTimesToOtherDays')"
              @click="applyDayTimesToOthers(dayIndex)"
            >
              <i class="fal fa-copy"></i>
              <span>{{ $t("MESSAGES.applyTimesToOtherDays") }}</span>
            </button>

            <div class="input_wrapper switch_wrapper day_switch">
              <v-switch
                color="green"
                :label="
                  day.is_active
                    ? $t('PLACEHOLDERS.active')
                    : $t('MESSAGES.dayHoliday')
                "
                v-model="day.is_active"
                hide-details
                inset
                :disabled="readonly"
                @change="onDayToggle(day)"
              />
            </div>
          </div>
        </div>

        <div v-if="day.is_active" class="day_card_body">
          <div
            v-for="(slot, slotIndex) in day.time_slots"
            :key="`${day.date}-${slotIndex}`"
            :ref="`slot-${day.date}-${slotIndex}`"
            class="slot_panel"
            :class="{
              'slot_panel--error':
                highlightedSlotKey === `${day.date}-${slotIndex}` ||
                !!slotErrorsMap[`${day.date}-${slotIndex}`]?.length,
            }"
          >
            <div class="slot_panel_header">
              <span class="slot_panel_title">
                {{ $t("MESSAGES.timeSlot") }} {{ slotIndex + 1 }}
              </span>
            </div>

            <div class="slot_fields_grid">
              <div class="slot_field_group">
                <div class="input_wrapper top_label slot_field">
                  <label class="form-label">
                    {{ $t("MESSAGES.Entry time (from)") }}
                    <span class="text-danger">*</span>
                  </label>
                  <div class="time_input_shell">
                    <i
                      class="fal fa-clock time_input_icon"
                      aria-hidden="true"
                    ></i>
                    <a-time-picker
                      class="festival_time_picker"
                      placeholder=""
                      format="HH:mm"
                      valueFormat="HH:mm"
                      :use12Hours="false"
                      :allowClear="false"
                      :disabled="readonly"
                      :value="toTimeInputValue(slot.start_time) || null"
                      :getPopupContainer="getTimePickerContainer"
                      @change="
                        (time, timeString) =>
                          onTimePickerChange(
                            day.date,
                            slotIndex,
                            slot,
                            'start_time',
                            time,
                            timeString
                          )
                      "
                    />
                  </div>
                </div>
              </div>

              <div class="slot_field_group">
                <div class="input_wrapper top_label slot_field">
                  <label class="form-label">
                    {{ $t("MESSAGES.Entry time (to)") }}
                    <span class="text-danger">*</span>
                  </label>
                  <div class="time_input_shell">
                    <i
                      class="fal fa-clock time_input_icon"
                      aria-hidden="true"
                    ></i>
                    <a-time-picker
                      class="festival_time_picker"
                      placeholder=""
                      format="HH:mm"
                      valueFormat="HH:mm"
                      :use12Hours="false"
                      :allowClear="false"
                      :disabled="readonly"
                      :value="toTimeInputValue(slot.end_time) || null"
                      :getPopupContainer="getTimePickerContainer"
                      @change="
                        (time, timeString) =>
                          onTimePickerChange(
                            day.date,
                            slotIndex,
                            slot,
                            'end_time',
                            time,
                            timeString
                          )
                      "
                    />
                  </div>
                </div>
              </div>

              <div class="slot_field_group">
                <div class="input_wrapper top_label slot_field">
                  <label class="form-label">
                    {{ $t("MESSAGES.maxTicketsPerSlot") }}
                    <span class="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    class="festival_number_input"
                    min="1"
                    :dir="isRtl ? 'rtl' : 'ltr'"
                    :value="slot.max_tickets"
                    :readonly="readonly"
                    :disabled="readonly"
                    @input="
                      onMaxTicketsInput(day.date, slotIndex, slot, $event)
                    "
                  />
                </div>
              </div>

              <div
                v-if="!readonly"
                class="slot_field_group slot_field_group--actions"
              >
                <button
                  v-if="day.time_slots.length > 1"
                  type="button"
                  class="slot_action_btn slot_action_btn--remove"
                  :title="$t('BUTTONS.delete')"
                  @click="removeTimeSlot(dayIndex, slotIndex)"
                >
                  <i class="fas fa-minus-circle"></i>
                </button>
                <button
                  v-if="slotIndex === day.time_slots.length - 1"
                  type="button"
                  class="slot_action_btn slot_action_btn--add"
                  :title="$t('BUTTONS.addTime')"
                  @click="addTimeSlot(dayIndex)"
                >
                  <i class="fas fa-plus-circle"></i>
                </button>
              </div>
            </div>

            <div
              v-if="
                !readonly && slotErrorsMap[`${day.date}-${slotIndex}`]?.length
              "
              class="slot_field_errors"
            >
              <p
                v-for="(error, errorIndex) in slotErrorsMap[
                  `${day.date}-${slotIndex}`
                ]"
                :key="errorIndex"
              >
                {{ error }}
              </p>
            </div>
          </div>
        </div>

        <div v-else class="day_card_holiday">
          <i class="fal fa-calendar-times"></i>
          <span>{{ $t("MESSAGES.dayExcludedHint") }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import moment from "moment";
import "moment/locale/ar";
import { mapGetters } from "vuex";
import {
  createDefaultSlot,
  getDaySlotErrors,
  normalizeTimeValue,
  syncSchedules,
} from "@/utils/festivalSchedule";

export default {
  name: "FestivalDailySchedule",

  props: {
    value: {
      type: Array,
      default: () => [],
    },
    startDate: {
      type: String,
      default: null,
    },
    endDate: {
      type: String,
      default: null,
    },
    festivalType: {
      type: Object,
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      localSchedules: [],
      isInternalUpdate: false,
      highlightedSlotKey: null,
      highlightedDayDate: null,
      highlightTimer: null,
      touchedSlots: {},
      showAllErrors: false,
    };
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
    isRtl() {
      return (this.getAppLocale || this.$i18n.locale) === "ar";
    },
    momentLocale() {
      return this.isRtl ? "ar" : "en";
    },
    showSchedule() {
      const type = this.festivalType?.id;
      if (!this.startDate || !type) return false;
      if (type === "extended") return !!this.endDate;
      return type === "limited";
    },
    slotErrorsMap() {
      if (this.readonly) return {};

      const map = {};

      this.localSchedules.forEach((day) => {
        if (!day.is_active) return;

        const dayErrors = getDaySlotErrors(
          day,
          (key, params) => this.$t(key, params),
          {
            getSlotOptions: (slotIndex) =>
              this.getSlotValidationOptions(day.date, slotIndex),
          }
        );

        Object.keys(dayErrors).forEach((slotIndex) => {
          const numericIndex = Number(slotIndex);

          if (!this.shouldDisplaySlotErrors(day.date, numericIndex)) {
            return;
          }

          map[`${day.date}-${slotIndex}`] = dayErrors[slotIndex];
        });
      });

      return map;
    },
  },

  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (this.isInternalUpdate) return;
        this.localSchedules = Array.isArray(newValue) ? [...newValue] : [];
      },
    },
    startDate() {
      this.syncFromDates();
    },
    endDate() {
      this.syncFromDates();
    },
    festivalType() {
      this.syncFromDates();
    },
    momentLocale() {
      this.$forceUpdate();
    },
  },

  methods: {
    formatDisplayDate(date) {
      const formatted = moment(date, "YYYY-MM-DD")
        .locale(this.momentLocale)
        .format("dddd، D MMMM YYYY");

      if (!this.isRtl) {
        return formatted.replace("،", ",");
      }

      return formatted;
    },
    formatDayChip(date) {
      return moment(date, "YYYY-MM-DD").locale(this.momentLocale).format("DD");
    },
    syncFromDates() {
      if (!this.showSchedule) {
        this.localSchedules = [];
        this.emitChange();
        return;
      }

      this.localSchedules = syncSchedules(
        this.localSchedules,
        this.startDate,
        this.endDate,
        this.festivalType?.id
      );
      this.emitChange();
    },
    onDayToggle(day) {
      if (day.is_active && !day.time_slots.length) {
        day.time_slots.push(createDefaultSlot());
      }
      this.emitChange();
    },
    canApplyDayTimes(sourceDayIndex) {
      const sourceDay = this.localSchedules[sourceDayIndex];
      if (!sourceDay?.is_active || !sourceDay.time_slots?.length) {
        return false;
      }

      return this.localSchedules.some(
        (day, index) => index !== sourceDayIndex && day.is_active
      );
    },
    cloneDayTimeSlots(timeSlots) {
      return (timeSlots || []).map((slot) => ({
        id: null,
        start_time: slot.start_time ?? null,
        end_time: slot.end_time ?? null,
        max_tickets: slot.max_tickets ?? null,
      }));
    },
    applyDayTimesToOthers(sourceDayIndex) {
      if (this.readonly || !this.canApplyDayTimes(sourceDayIndex)) return;

      const sourceDay = this.localSchedules[sourceDayIndex];
      const clonedSlots = this.cloneDayTimeSlots(sourceDay.time_slots);

      this.localSchedules.forEach((day, index) => {
        if (index === sourceDayIndex || !day.is_active) return;
        day.time_slots = this.cloneDayTimeSlots(clonedSlots);
      });

      this.emitChange();
      this.$message.success(this.$t("MESSAGES.timesAppliedToOtherDays"));
    },
    addTimeSlot(dayIndex) {
      this.localSchedules[dayIndex].time_slots.push(createDefaultSlot());
      this.emitChange();
    },
    removeTimeSlot(dayIndex, slotIndex) {
      this.localSchedules[dayIndex].time_slots.splice(slotIndex, 1);
      this.emitChange();
    },
    onMaxTicketsInput(dayDate, slotIndex, slot, event) {
      slot.max_tickets = event.target.value;
      this.markSlotTouched(dayDate, slotIndex, "max_tickets");
      this.emitChange();
    },
    toTimeInputValue(time) {
      return normalizeTimeValue(time) || "";
    },
    onTimePickerChange(dayDate, slotIndex, slot, field, time, timeString) {
      const normalized = normalizeTimeValue(timeString || time);
      slot[field] = normalized;
      this.markSlotTouched(dayDate, slotIndex, field);
      this.emitChange();
    },
    getSlotTouchKey(dayDate, slotIndex, field) {
      return `${dayDate}-${slotIndex}-${field}`;
    },
    markSlotTouched(dayDate, slotIndex, field) {
      this.$set(
        this.touchedSlots,
        this.getSlotTouchKey(dayDate, slotIndex, field),
        true
      );
    },
    isSlotTouched(dayDate, slotIndex) {
      return ["start_time", "end_time", "max_tickets"].some(
        (field) =>
          this.touchedSlots[this.getSlotTouchKey(dayDate, slotIndex, field)]
      );
    },
    getSlotValidationOptions(dayDate, slotIndex) {
      return {
        showAll: this.showAllErrors,
        touched: {
          start_time:
            !!this.touchedSlots[
              this.getSlotTouchKey(dayDate, slotIndex, "start_time")
            ],
          end_time:
            !!this.touchedSlots[
              this.getSlotTouchKey(dayDate, slotIndex, "end_time")
            ],
          max_tickets:
            !!this.touchedSlots[
              this.getSlotTouchKey(dayDate, slotIndex, "max_tickets")
            ],
        },
      };
    },
    shouldDisplaySlotErrors(dayDate, slotIndex) {
      return this.showAllErrors || this.isSlotTouched(dayDate, slotIndex);
    },
    showAllValidation() {
      this.showAllErrors = true;
    },
    getTimePickerContainer(triggerNode) {
      return triggerNode.parentNode;
    },
    focusScheduleError({ dayDate = null, slotIndex = null } = {}) {
      this.showAllErrors = true;
      this.clearHighlight();

      this.$nextTick(() => {
        let target = null;

        if (dayDate != null && slotIndex != null) {
          target = this.$refs[`slot-${dayDate}-${slotIndex}`];
          this.highlightedSlotKey = `${dayDate}-${slotIndex}`;
        } else if (dayDate != null) {
          target = this.$refs[`day-${dayDate}`];
          this.highlightedDayDate = dayDate;
        } else {
          target = this.$refs.scheduleHeader;
        }

        if (Array.isArray(target)) {
          target = target[0];
        }

        if (target?.scrollIntoView) {
          target.scrollIntoView({ behavior: "smooth", block: "center" });
        }

        this.highlightTimer = setTimeout(() => {
          this.clearHighlight();
        }, 4000);
      });
    },
    clearHighlight() {
      if (this.highlightTimer) {
        clearTimeout(this.highlightTimer);
        this.highlightTimer = null;
      }

      this.highlightedSlotKey = null;
      this.highlightedDayDate = null;
    },
    emitChange() {
      if (this.readonly) return;

      this.isInternalUpdate = true;
      this.$emit("input", this.localSchedules);
      this.$nextTick(() => {
        this.isInternalUpdate = false;
      });
    },
  },

  beforeDestroy() {
    this.clearHighlight();
  },
};
</script>

<style scoped>
.festival_daily_schedule {
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
  overflow: visible;
}

.festival_daily_schedule.is-readonly .slot_field,
.festival_daily_schedule.is-readonly .day_switch >>> .v-input--is-disabled {
  opacity: 1;
}

.festival_daily_schedule.is-readonly
  .festival_time_picker.ant-time-picker-disabled,
.festival_daily_schedule.is-readonly .festival_number_input:disabled {
  cursor: default;
}

.schedule_header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgba(64, 89, 80, 0.08) 0%,
    rgba(64, 89, 80, 0.02) 100%
  );
  border: 1px solid rgba(64, 89, 80, 0.12);
}

.schedule_header_icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--main_theme_clr, #405950);
  color: var(--white_clr, #fff);
  flex-shrink: 0;
}

.schedule_header_icon i {
  font-size: 20px;
}

.schedule_title {
  margin: 0 0 0.25rem;
  color: var(--main_theme_clr, #405950);
  font-size: 1.1rem;
  font-weight: 700;
}

.schedule_subtitle {
  margin: 0;
  color: var(--theme_secondary_text_clr, #8d8d8d);
  font-size: 0.9rem;
}

.schedule_placeholder {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  border: 1px dashed rgba(64, 89, 80, 0.25);
  background: var(--secondary_theme_clr, #f7f7f7);
  color: var(--theme_secondary_text_clr, #8d8d8d);
  font-size: 0.95rem;
  line-height: 1.5;
}

.schedule_placeholder i {
  color: var(--main_theme_clr, #405950);
  font-size: 1.1rem;
  flex-shrink: 0;
}

.is-rtl .schedule_placeholder {
  text-align: right;
}

.festival_daily_schedule:not(.is-rtl) .schedule_placeholder {
  text-align: left;
}

.day_card {
  border: 1px solid var(--border_clr, #e0e0e0);
  border-radius: 14px;
  padding: 0;
  margin-bottom: 1rem;
  background: var(--theme_second_bg_clr, #fff);
  box-shadow: 0 4px 18px var(--simple_shadow_clr, rgba(0, 0, 0, 0.06));
  overflow: visible;
  transition: opacity 0.25s ease, box-shadow 0.25s ease;
}

.day_card--inactive {
  opacity: 0.82;
  background: var(--secondary_theme_clr, #f7f7f7);
}

.day_card--error {
  border-color: rgba(220, 53, 69, 0.45);
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
}

.day_card_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: var(--secondary_theme_clr, #f7f7f7);
  border-bottom: 1px solid var(--border_clr, #e8e8e8);
  border-radius: 14px 14px 0 0;
  overflow: hidden;
}

.is-rtl .day_card_header {
  flex-direction: row;
}

.day_date_block {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
}

.day_date_chip {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--main_theme_clr, #405950);
  color: var(--white_clr, #fff);
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
}

.day_date {
  font-weight: 600;
  color: var(--theme_text_clr, #313131);
  font-size: 0.98rem;
  line-height: 1.4;
}

.day_header_actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.apply_times_btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.85rem;
  border-radius: 10px;
  border: 1px solid rgba(64, 89, 80, 0.3);
  background: var(--theme_second_bg_clr, #fff);
  color: var(--main_theme_clr, #405950);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.apply_times_btn:hover {
  transform: translateY(-1px);
  border-color: var(--main_theme_clr, #405950);
  box-shadow: 0 4px 10px var(--simple_shadow_clr, rgba(0, 0, 0, 0.08));
}

.apply_times_btn i {
  font-size: 0.95rem;
}

.day_switch {
  flex-shrink: 0;
  margin: 0;
}

.day_switch >>> .v-input {
  margin: 0;
}

.day_switch >>> .v-input__control .v-input__slot {
  direction: ltr;
  display: flex;
  flex-direction: row-reverse;
  column-gap: 12px;
  margin-bottom: 0;
}

.day_switch >>> .v-input--selection-controls__input {
  margin: 0;
}

.day_switch >>> .v-label {
  color: var(--theme_text_clr, #313131);
  font-size: 15px;
  white-space: nowrap;
}

.day_card_body {
  padding: 1rem 1.25rem 1.25rem;
  overflow: visible;
}

.slot_panel {
  padding: 1rem;
  margin-bottom: 0.85rem;
  border-radius: 12px;
  background: var(--theme_main_bg_clr, #fafafa);
  border: 1px dashed rgba(64, 89, 80, 0.18);
  overflow: visible;
  position: relative;
}

.slot_panel:last-child {
  margin-bottom: 0;
}

.slot_panel--error {
  border-color: rgba(220, 53, 69, 0.55);
  background: rgba(220, 53, 69, 0.06);
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.12);
}

.slot_field_errors {
  margin-top: 0.75rem;
}

.slot_field_errors p {
  margin: 0 0 0.35rem;
  color: var(--mid_red_clr, #dc3545);
  font-size: 0.88rem;
  line-height: 1.5;
}

.slot_field_errors p:last-child {
  margin-bottom: 0;
}

.is-rtl .slot_field_errors {
  text-align: right;
}

.festival_daily_schedule:not(.is-rtl) .slot_field_errors {
  text-align: left;
}

.slot_panel_header {
  margin-bottom: 0.75rem;
}

.is-rtl .slot_panel_header {
  text-align: right;
}

.festival_daily_schedule:not(.is-rtl) .slot_panel_header {
  text-align: left;
}

.slot_panel_title {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.75rem;
  border-radius: 999px;
  background: rgba(64, 89, 80, 0.1);
  color: var(--main_theme_clr, #405950);
  font-size: 0.85rem;
  font-weight: 600;
}

.slot_fields_grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.15fr) auto;
  gap: 1rem;
  align-items: end;
}

.slot_field_group {
  min-width: 0;
}

.slot_field_group--actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  min-height: 44px;
  padding-bottom: 2px;
}

.slot_field {
  min-height: 44px;
  position: relative;
  border: 1px solid var(--main_theme_clr, #405950);
  border-radius: 8px;
  padding: 0;
  background: var(--theme_inputs_clr, #fff);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
  overflow: visible;
}

.slot_field:focus-within {
  border-color: var(--dark_main_theme_clr, #2f423a);
  box-shadow: 0 0 0 3px rgba(64, 89, 80, 0.12);
  z-index: 40;
}

.slot_field .form-label {
  position: absolute;
  top: -11px;
  right: 16px;
  left: auto;
  padding-inline: 8px;
  font-size: 13px;
  background-color: var(--theme_main_bg_clr, #fafafa);
  color: var(--main_theme_clr, #405950);
  z-index: 2;
  margin: 0;
  white-space: nowrap;
  text-align: start;
}

.festival_daily_schedule:not(.is-rtl) .slot_field .form-label {
  right: auto;
  left: 16px;
}

.time_input_shell {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 44px;
}

.time_input_shell .festival_time_picker {
  position: relative;
  z-index: 0;
  width: 100%;
}

.festival_time_picker >>> .ant-time-picker-input {
  width: 100%;
  min-height: 44px;
  padding: 10px 2.5rem 10px 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-size: 16px;
  color: var(--theme_text_clr, #313131);
  direction: ltr;
  text-align: left;
  box-shadow: none;
}

.is-rtl .festival_time_picker >>> .ant-time-picker-input {
  text-align: right;
  padding: 10px 12px 10px 2.5rem;
}

.festival_time_picker >>> .ant-time-picker-icon {
  display: none;
}

.time_input_icon {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 12px;
  color: var(--main_theme_clr, #405950);
  font-size: 15px;
  pointer-events: none;
  opacity: 0.9;
  z-index: 1;
}

.is-rtl .time_input_icon {
  right: auto;
  left: 12px;
}

.festival_number_input {
  width: 100%;
  min-height: 44px;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-size: 16px;
  color: var(--theme_text_clr, #313131);
  outline: none;
}

.is-rtl .festival_number_input {
  text-align: right;
}

.festival_daily_schedule:not(.is-rtl) .festival_number_input {
  text-align: left;
}

.festival_number_input:focus {
  outline: none;
}

.slot_action_btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--theme_second_bg_clr, #fff);
  border: 1px solid var(--border_clr, #e0e0e0);
  padding: 0;
  cursor: pointer;
  line-height: 1;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.slot_action_btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px var(--simple_shadow_clr, rgba(0, 0, 0, 0.08));
}

.slot_action_btn i {
  font-size: 20px;
}

.slot_action_btn--add {
  border-color: rgba(64, 89, 80, 0.35);
}

.slot_action_btn--add i {
  color: var(--main_theme_clr, #405950);
}

.slot_action_btn--remove {
  border-color: rgba(220, 53, 69, 0.25);
}

.slot_action_btn--remove i {
  color: var(--mid_red_clr, #dc3545);
}

.day_card_holiday {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 1rem 1.25rem 1.25rem;
  color: var(--light_gray_clr, #8d8d8d);
  font-size: 0.92rem;
}

.day_card_holiday i {
  font-size: 1.1rem;
  color: var(--mid_red_clr, #dc3545);
}

@media (max-width: 1200px) {
  .slot_fields_grid {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  .slot_field_group--actions {
    grid-column: 1 / -1;
    justify-content: flex-start;
    min-height: auto;
    padding-top: 0.25rem;
  }

  .is-rtl .slot_field_group--actions {
    justify-content: flex-end;
  }
}

@media (max-width: 768px) {
  .slot_fields_grid {
    grid-template-columns: 1fr;
  }

  .day_card_header {
    flex-direction: column;
    align-items: flex-start;
  }

  .is-rtl .day_card_header {
    align-items: flex-end;
  }

  .day_header_actions {
    width: 100%;
    justify-content: space-between;
  }

  .apply_times_btn {
    white-space: normal;
    text-align: start;
  }

  .day_switch {
    width: auto;
    margin-inline-start: auto;
  }

  .day_switch >>> .v-input__control .v-input__slot {
    justify-content: space-between;
    width: 100%;
  }
}
</style>
